import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { AlertTriangle, Loader2 } from 'lucide-react';

/**
 * Refund / Cancel order (Ryan, 2026-10-01; server.py refund_project / cancel_order, app/refund_box.py).
 *
 * Shows what the client paid for what before anything moves: each line at list price and at what they paid after
 * the code and gem-tier discounts, the rest of the order, refunds so far. A refund is picked by line items (each
 * at its discounted price, so 1 of 3 edits bought with 65% off refunds the 35% they paid) or typed. When the
 * refund leaves the whole order refunded, it asks, every time, about the referral reward the order earned and the
 * discount code it used. The client is emailed a receipt unless unticked. Cancel mode also settles the editor's
 * pay as it actually stands: pay recorded at first draft is kept, changed or zeroed, never doubled.
 *
 * What can be refunded is the server's answer (server.py _payment_received), never the order's price: an order that
 * was never paid comes back with nothing to refund, and the box offers only to cancel it (Ryan, 2026-10-07).
 */
const money = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const when = (iso) => { try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); } catch { return ''; } };
const SOURCE = { stripe: 'in Stripe', portal: 'from the portal', elsewhere: 'outside Stripe' };
const Row = ({ label, value, strong, muted, testid }) => (
  <div className={`flex justify-between gap-3 ${muted ? 'text-slate-400' : ''}`} data-testid={testid}>
    <span className="min-w-0">{label}</span><span className={strong ? 'text-zinc-100 font-semibold' : 'text-zinc-100/90'}>{value}</span>
  </div>
);

function Choice({ value, label, choice, onPick, children }) {
  return (
    <label className={`tap-auto flex items-start gap-2.5 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors ${
      choice === value ? 'border-ug-gold/40 bg-ug-gold/[0.05]' : 'border-ug-border hover:border-ug-border-strong'}`} data-testid={`refund-option-${value}`}>
      {/* sized by hand: the site's field rules give every input a field's height and a 44px tap width */}
      <input type="radio" name="refund-choice" value={value} checked={choice === value} onChange={() => onPick(value)}
        style={{ width: 16, height: 16, minHeight: 0, minWidth: 0, padding: 0, margin: '2px 0 0 0', flex: '0 0 16px' }}
        className="tap-auto accent-[#2bb3a8] bg-transparent" />
      <span className="flex-1 min-w-0"><span className="block text-[13px] text-zinc-100">{label}</span>{children}</span>
    </label>
  );
}

function Tick({ checked, onChange, testid, children }) {
  return (
    <label className="tap-auto flex items-start gap-2 cursor-pointer text-[12px] text-zinc-100/90" data-testid={testid}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
        style={{ width: 15, height: 15, minHeight: 0, minWidth: 0, padding: 0, margin: '2px 0 0 0', flex: '0 0 15px' }}
        className="tap-auto accent-[#2bb3a8]" />
      <span className="leading-snug">{children}</span>
    </label>
  );
}

export function RefundDialog({ project, mode = 'refund', open, onOpenChange, onDone }) {
  const cancelMode = mode === 'cancel';
  const [p, setP] = useState(null);
  const [failed, setFailed] = useState('');
  const [choice, setChoice] = useState('rest');      // rest | part | none | elsewhere
  const [byItems, setByItems] = useState(false);
  const [picked, setPicked] = useState([]);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [emailClient, setEmailClient] = useState(true);
  const [giveBackCode, setGiveBackCode] = useState(false);
  const [takeBack, setTakeBack] = useState(false);
  const [payMode, setPayMode] = useState('keep');    // keep | set
  const [pay, setPay] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => {
    setP(null); setFailed('');
    api.get(`/projects/${project.id}/refund-preview`).then(r => {
      const d = r.data; setP(d);
      const stripe = d.paid_via === 'stripe';
      setChoice(d.remaining > 0 ? (stripe ? (cancelMode ? 'rest' : 'part') : 'elsewhere') : 'none');
      setByItems(false); setPicked([]); setAmount(''); setEmailClient(!!d.client?.has_email);
      setGiveBackCode(false); setTakeBack(false); setPayMode('keep'); setPay('');
    }).catch(e => setFailed(e?.response?.data?.detail || 'Could not load this order'));
  };
  useEffect(() => { if (open && project?.id) { setReason(''); setSaving(false); load(); } }, [open, project?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const lines = useMemo(() => p?.breakdown?.lines || [], [p]);
  const pickedSum = useMemo(() => lines.filter(l => picked.includes(l.index)).reduce((a, l) => a + l.net, 0), [lines, picked]);
  const partial = choice === 'part' || choice === 'elsewhere';
  const refundNow = !p ? 0 : choice === 'rest' ? p.remaining : partial ? (byItems ? Math.round(pickedSum * 100) / 100 : Number(amount || 0)) : 0;
  const tooMuch = partial && refundNow > (p?.remaining || 0) + 0.005;
  const nothing = partial && !(refundNow > 0);
  const orderLeft = p ? Math.max(0, Math.round((p.order.remaining - refundNow) * 100) / 100) : 0;
  const completesOrder = !!p && refundNow > 0 && orderLeft <= 0.005;
  const regular = (p?.editor?.recorded || []).find(r => r.kind === 'delivery');
  const paidRow = (p?.editor?.recorded || []).find(r => r.paid);
  const payNum = Number(pay || 0);
  const blocked = !p || saving || tooMuch || nothing || (!cancelMode && !(refundNow > 0));

  const submit = async () => {
    if (blocked) return;
    setSaving(true);
    const common = { reason: reason.trim(), email_client: emailClient && refundNow > 0,
                     give_back_code: completesOrder && giveBackCode, take_back_reward: completesOrder && takeBack,
                     lines: partial && byItems ? picked : [] };
    try {
      let r;
      if (cancelMode) {
        r = await api.post(`/projects/${project.id}/cancel`, { ...common, refund: choice,
          amount: partial && !byItems ? refundNow : null,
          editor_pay: p.editor && !paidRow && payMode === 'set' ? payNum : null });
      } else {
        r = await api.post(`/projects/${project.id}/refund`, { ...common, method: choice === 'elsewhere' ? 'elsewhere' : 'stripe',
          amount: byItems ? null : refundNow });
      }
      const d = r.data || {};
      const bits = [refundNow > 0 ? `${money(refundNow)} refunded` : null, cancelMode ? 'order cancelled' : null,
        d.emailed ? 'receipt emailed' : null, d.code?.done ? `${d.code.code} use given back` : null,
        ...(d.rewards || []).map(x => x.done ? `$${x.amount} reward taken back` : `reward kept (${x.why})`)].filter(Boolean);
      toast.success(bits.join(', ').replace(/^./, c => c.toUpperCase()) + '.');
      onOpenChange(false); onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Nothing was changed');
      setSaving(false);
    }
  };

  const moveHere = async (rid) => {
    try { await api.post(`/projects/${project.id}/refunds/${rid}/move-here`); toast.success('Refund moved onto this edit'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not move it'); }
  };

  const b = p?.breakdown;
  // Said from the admin's side: the client by name gets money back; Uncut Gems keeps the rest. Never "they".
  const clientName = p?.client?.name || 'the client';
  const ClientName = clientName.charAt(0).toUpperCase() + clientName.slice(1);
  const orderRows = p?.order?.projects || [];
  const titlesDiffer = new Set(orderRows.map(o => o.title)).size > 1;
  const othersKept = orderRows.filter(o => !o.this && o.paid && o.remaining > 0.005);
  const othersSum = othersKept.reduce((a, o) => a + o.remaining, 0);
  const togglePick = (i) => setPicked(x => x.includes(i) ? x.filter(y => y !== i) : [...x, i]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); }}>
      <DialogContent className="bg-ug-raised border-ug-border-strong max-w-lg max-h-[90vh] overflow-y-auto" data-testid="refund-dialog">
        <DialogHeader>
          <DialogTitle className="text-zinc-100">{cancelMode ? `Cancel "${project?.title || 'this order'}"?` : `Refund on "${project?.title || 'this project'}"`}</DialogTitle>
          <DialogDescription className="text-slate-400 text-[12px] leading-relaxed">
            {cancelMode ? 'It leaves every board and its history is kept. ' : 'The job carries on. '}
            Revenue, analytics and the client&apos;s spend count only what is not refunded.
          </DialogDescription>
        </DialogHeader>

        {failed && <p className="text-[12px] text-ug-status-red" data-testid="refund-error">{failed}</p>}
        {!p && !failed && <div className="flex items-center gap-2 text-[12px] text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Checking Stripe for refunds already made…</div>}

        {p && (
          <div className="flex flex-col gap-4">
            {/* What they paid for this project, line by line */}
            <div className="rounded-lg border border-ug-border bg-ug-surface/40 px-3 py-2.5 text-[12px] text-slate-300 space-y-1" data-testid="refund-breakdown">
              <div className="font-sans text-[9px] font-semibold uppercase tracking-wider text-slate-500 mb-1">What {clientName} {p.paid ? 'paid' : 'ordered'} for this {p.order.projects.length > 1 ? 'edit' : 'order'}</div>
              {lines.map(l => (
                <div key={l.index} className="flex justify-between gap-3" data-testid={`refund-line-${l.index}`}>
                  <span className="min-w-0 truncate">{l.label}{l.refunded && <span className="ml-1.5 text-[10px] text-amber-300" data-tone="warning">refunded</span>}</span>
                  <span className="shrink-0">{l.list !== l.net && <span className="text-slate-500 line-through mr-1.5">{money(l.list)}</span>}<span className="text-zinc-100/90">{money(l.net)}</span></span>
                </div>
              ))}
              {b.surcharge > 0 && <Row muted label="Multi-day surcharge (in the prices above)" value={`+${money(b.surcharge)}`} />}
              <div className="border-t border-ug-border-subtle pt-1 mt-1 space-y-1">
                <Row muted label="List price" value={money(b.subtotal + b.surcharge)} />
                {b.discount_amount > 0 && <Row muted testid="refund-code-row"
                  label={b.two_for_one ? 'Second version (2-for-1 offer)' : `Code ${b.discount_code || ''}${b.discount_percent ? ` (${b.discount_percent}% off)` : ''}`}
                  value={`-${money(b.discount_amount)}`} />}
                {b.tier_discount_amount > 0 && <Row muted label={`Gem tier${b.tier_name ? ` (${b.tier_name})` : ''}`} value={`-${money(b.tier_discount_amount)}`} />}
                <Row label="Paid" value={p.paid ? money(p.total) : 'Not paid'} strong testid="refund-paid" />
              </div>
              {(p.refunds || []).map((r, i) => (
                <div key={i} className="flex justify-between gap-3 text-slate-400">
                  <span className="min-w-0">Refunded {SOURCE[r.source] || ''} {when(r.at)}
                    {r.split_of && r.split_of !== r.amount && r.source === 'stripe' && (
                      <span className="block text-[10px] text-slate-500">Part of a {money(r.split_of)} Stripe refund, split by price.{' '}
                        <button type="button" onClick={() => moveHere(r.stripe_refund_id)} data-testid={`refund-move-here-${i}`}
                          className="tap-auto underline underline-offset-2 hover:text-zinc-100">Put it all on this edit</button></span>)}
                  </span><span>-{money(r.amount)}</span>
                </div>
              ))}
              {p.paid && <div className="border-t border-ug-border-subtle pt-1"><Row label="Still paid" value={money(p.remaining)} strong testid="refund-remaining" /></div>}
            </div>

            {p.order.projects.length > 1 && (
              <div className="rounded-lg border border-ug-border px-3 py-2.5 text-[12px] text-slate-300 space-y-1" data-testid="refund-order">
                <div className="font-sans text-[9px] font-semibold uppercase tracking-wider text-slate-500 mb-1">The whole order</div>
                {p.order.projects.map(o => (
                  <div key={o.id} className={`flex justify-between gap-3 ${o.this ? 'text-zinc-100' : ''}`} data-testid={`refund-order-row-${o.id}`}>
                    <span className="min-w-0">
                      <span className="block truncate">{o.edit || o.title}{o.this && <span className="text-slate-500"> (this one)</span>}{o.cancelled && <span className="text-amber-300" data-tone="warning"> cancelled</span>}</span>
                      {o.edit && titlesDiffer && <span className="block truncate text-[11px] text-slate-500">{o.title}</span>}
                    </span>
                    <span className="shrink-0">{money(o.total)}{o.refunded > 0 && <span className="text-slate-500"> (-{money(o.refunded)})</span>}</span>
                  </div>
                ))}
                <div className="border-t border-ug-border-subtle pt-1"><Row label="Order paid" value={`${money(p.order.total)}${p.order.refunded > 0 ? `, ${money(p.order.refunded)} refunded` : ''}`} /></div>
              </div>
            )}

            {/* How much */}
            {p.remaining > 0 ? (
              <div className="flex flex-col gap-1.5" role="radiogroup" aria-label="Refund">
                {p.paid_via === 'stripe' && cancelMode && <Choice choice={choice} onPick={setChoice} value="rest" label={`Refund the rest: ${money(p.remaining)}`}>
                  <span className="block text-[11px] text-slate-500">Through Stripe, back to {clientName}&apos;s card.</span></Choice>}
                {(p.paid_via === 'stripe' || p.paid_via === 'outside') && (
                  <Choice choice={choice} onPick={setChoice} value={p.paid_via === 'stripe' ? 'part' : 'elsewhere'}
                    label={p.paid_via === 'stripe' ? (cancelMode ? 'Refund part of it' : 'Refund through Stripe') : 'Refunded somewhere else'}>
                    {p.paid_via === 'outside' && <span className="block text-[11px] text-slate-500">Not paid through Stripe: refund it where it was paid; this records it.</span>}
                    {partial && (
                      <span className="block mt-1.5 space-y-1.5" onClick={e => e.stopPropagation()}>
                        {lines.length > 0 && (
                          <span className="flex gap-3 text-[11px]">
                            <button type="button" onClick={() => setByItems(false)} data-testid="refund-by-amount" className={`tap-auto ${!byItems ? 'text-oxidized-teal underline' : 'text-slate-400'}`}>Type an amount</button>
                            <button type="button" onClick={() => setByItems(true)} data-testid="refund-by-items" className={`tap-auto ${byItems ? 'text-oxidized-teal underline' : 'text-slate-400'}`}>Pick items</button>
                          </span>
                        )}
                        {byItems ? lines.map(l => (
                          <Tick key={l.index} checked={picked.includes(l.index)} onChange={() => !l.refunded && togglePick(l.index)} testid={`refund-pick-${l.index}`}>
                            <span className={l.refunded ? 'line-through text-slate-500' : ''}>{l.label}: {money(l.net)}</span>{l.refunded && ' (already refunded)'}
                          </Tick>
                        )) : (
                          <span className="flex items-center gap-2">
                            <span className="text-slate-400 text-[13px]">$</span>
                            <input type="number" min="0.01" max={p.remaining} step="0.01" inputMode="decimal" value={amount}
                              onChange={e => setAmount(e.target.value)} data-testid="refund-amount"
                              className="tap-auto w-28 h-8 rounded-md bg-ug-surface border border-ug-border text-zinc-100 text-[13px] px-2 outline-none focus:border-ug-gold/40" />
                            <span className="text-[11px] text-slate-500">up to {money(p.remaining)}</span>
                          </span>
                        )}
                      </span>
                    )}
                  </Choice>
                )}
                {cancelMode && <Choice choice={choice} onPick={setChoice} value="none" label="No refund">
                  <span className="block text-[11px] text-slate-500">Cancel the work. Uncut Gems keeps what was paid.</span></Choice>}
              </div>
            ) : (
              <p className="text-[12px] text-slate-400" data-testid="refund-nothing-left">{p.paid ? 'Nothing left to refund on this project.' : 'This order was never paid, so there is nothing to refund.'}</p>
            )}

            {refundNow > 0 && !tooMuch && (
              <div className="text-[12px] text-zinc-100/90 leading-relaxed space-y-0.5" data-testid="refund-summary">
                <p>{ClientName} gets <strong>{money(refundNow)}</strong> back{refundNow >= p.remaining - 0.005 && p.remaining >= p.total - 0.005
                  ? `: everything paid for this ${p.order.projects.length > 1 ? 'edit' : 'order'}.` : ` of the ${money(p.total)} paid for this ${p.order.projects.length > 1 ? 'edit' : 'order'}.`}</p>
                <p data-testid="refund-summary-kept">Uncut Gems keeps {money(Math.max(0, p.remaining - refundNow))} of this {p.order.projects.length > 1 ? 'edit' : 'order'}{p.order.projects.length > 1 ? `, and ${money(orderLeft)} of the whole order` : ''}.</p>
                {othersKept.length > 0 && (
                  <p className="text-slate-400" data-testid="refund-summary-others">{othersKept.length === 1
                    ? `The ${othersKept[0].edit || othersKept[0].title} (${money(othersSum)}) is not refunded.`
                    : `The other ${othersKept.length} edits (${money(othersSum)}) are not refunded.`}</p>
                )}
              </div>
            )}
            {(tooMuch || (partial && nothing && (amount || picked.length))) && (
              <p className="text-[12px] text-amber-300 flex items-center gap-1.5" data-tone="warning" data-testid="refund-amount-warning">
                <AlertTriangle className="w-3.5 h-3.5" /> Choose an amount up to {money(p.remaining)}.
              </p>
            )}

            {/* Asked every time, only when this refund leaves the whole order refunded */}
            {completesOrder && (p.code?.used_on_this_order || (p.rewards || []).length > 0) && (
              <div className="flex flex-col gap-2 rounded-lg border border-ug-border px-3 py-2.5" data-testid="refund-order-complete">
                <span className="text-[11px] text-slate-400">This refunds the whole order.</span>
                {p.code?.used_on_this_order && (
                  <Tick checked={giveBackCode} onChange={setGiveBackCode} testid="refund-give-back-code">
                    Give back their use of {p.code.code}{p.code.kind === 'referral' ? ' (their friend referral code)' : p.code.kind === 'referral_reward' ? ' (a referral reward code)' : ''} so they can use it on a real order
                  </Tick>
                )}
                {(p.rewards || []).map(r => r.used ? (
                  <p key={r.tier} className="text-[12px] text-slate-400" data-testid={`refund-reward-used-${r.tier}`}>This order earned {r.referrer || 'their referrer'} a ${r.amount} reward, already used, so it stays.</p>
                ) : (
                  <Tick key={r.tier} checked={takeBack} onChange={setTakeBack} testid={`refund-take-back-${r.tier}`}>
                    Take back the ${r.amount} reward this order earned {r.referrer || 'their referrer'} ({r.code}, not used yet)
                  </Tick>
                ))}
              </div>
            )}

            {cancelMode && p.editor && (
              <div className="flex flex-col gap-1.5" data-testid="refund-editor">
                <span className="text-[12px] text-zinc-100">{p.editor.name}&apos;s pay for this job</span>
                {paidRow ? (
                  <span className="text-[11px] text-slate-400">Already paid {money(paidRow.amount)}. That stays.</span>
                ) : (
                  <>
                    {regular && <span className="text-[11px] text-slate-400">Recorded at first draft: {money(regular.amount)}, not paid yet.</span>}
                    <span className="flex flex-wrap gap-3 text-[12px]">
                      <Tick checked={payMode === 'keep'} onChange={() => setPayMode('keep')} testid="refund-pay-keep">{regular ? `Keep ${money(regular.amount)}` : 'Nothing'}</Tick>
                      <Tick checked={payMode === 'set'} onChange={() => setPayMode('set')} testid="refund-pay-set">{regular ? 'Change to' : 'Pay for work done'}</Tick>
                      {payMode === 'set' && (
                        <span className="flex items-center gap-1"><span className="text-slate-400">$</span>
                          <input type="number" min="0" step="1" value={pay} onChange={e => setPay(e.target.value)} placeholder="0" data-testid="refund-pay-amount"
                            className="tap-auto w-24 h-7 rounded-md bg-ug-surface border border-ug-border text-zinc-100 text-[12px] px-2 outline-none focus:border-ug-gold/40" /></span>
                      )}
                    </span>
                    <span className="text-[11px] text-slate-500">{p.editor.delivered ? 'They have delivered a draft.' : 'No draft delivered yet.'}{p.editor.expected_pay > 0 ? ` Full pay for this job would be ${money(p.editor.expected_pay)}.` : ''} They are told in the job&apos;s chat.</span>
                  </>
                )}
              </div>
            )}

            {refundNow > 0 && (
              <Tick checked={emailClient} onChange={setEmailClient} testid="refund-email-client">
                {p.client?.has_email ? `Email ${p.client.name || 'the client'} a refund receipt` : 'No email address on file for the client'}
              </Tick>
            )}

            <div className="flex flex-col gap-1.5">
              <span className="text-[12px] text-zinc-100">Why <span className="text-slate-500">(only the team sees this)</span></span>
              <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} maxLength={500}
                placeholder={cancelMode ? 'Client cancelled the wedding' : 'Teaser dropped from the order'} data-testid="refund-reason"
                className="w-full rounded-md bg-ug-surface border border-ug-border text-zinc-100 text-[13px] p-2 outline-none focus:border-ug-gold/40 resize-none" />
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={() => onOpenChange(false)} disabled={saving} data-testid="refund-keep"
                className="tap-auto px-3 py-2 rounded-md border border-ug-border text-[12px] text-slate-300 hover:text-zinc-100">{cancelMode ? 'Keep the order' : 'Close'}</button>
              <button type="button" onClick={submit} disabled={blocked} data-testid="refund-confirm" data-tone="warning"
                className="tap-auto px-3 py-2 rounded-md border border-amber-500/60 bg-amber-500/[0.12] text-amber-300 text-[12px] font-semibold hover:bg-amber-500/20 disabled:opacity-50 inline-flex items-center gap-1.5">
                {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {cancelMode ? (refundNow > 0 ? `Cancel and refund ${money(refundNow)}` : 'Cancel order') : `Refund ${money(refundNow)}`}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
